// ═══════════════════════════════════════════════════════════════
// VolunteerOS — Event Map
// Leaflet map view of volunteer events with sidebar list
// ═══════════════════════════════════════════════════════════════

import { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text, Group, Paper, Badge, Stack, TextInput, ScrollArea,
  Loader, Center, ActionIcon, Button, Divider, Modal, Title,
} from '../../components/gab/GabComponents';
import { IconSearch, IconMapPin, IconRefresh, IconUsers, IconCalendarEvent, IconShieldCheck } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useVolunteerStore, parseExtras } from '../../store/volunteerStore';
import type { VolEvent } from '../../store/volunteerStore';
import { useAuthStore } from '../../store/authStore';

// ── Fix default Leaflet marker icons ────────────────────────

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// ── Incident color palette ──────────────────────────────────

const INCIDENT_PALETTE = [
  'var(--color-primary-800)', 'var(--color-error)', '#07963F', '#A67103', '#7c3aed',
  '#0891b2', '#db2777', '#65a30d', '#ea580c', '#6366f1',
];

function createColoredIcon(color: string) {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      width: 24px; height: 24px; border-radius: 50%;
      background: ${color}; border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -14],
  });
}

const STATUS_COLORS: Record<string, string> = {
  planned: 'var(--color-primary-700)',
  active: 'var(--color-success)',
  closed: '#939598',
};

const TYPE_COLORS: Record<string, string> = {
  distribution: '#03AC9E',
  mucking: '#F86A0B',
  medical: 'var(--color-error)',
  intake: 'var(--data-viz-100)',
  command: 'var(--data-viz-650)',
  shelter: '#C68700',
  other: '#939598',
};

// ── FlyTo helper ────────────────────────────────────────────

function FlyTo({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    map.flyTo([lat, lng], 14, { duration: 1 });
  }, [lat, lng, map]);
  return null;
}

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 14 });
    }
  }, [points, map]);
  return null;
}

// ── Types ───────────────────────────────────────────────────

interface GeoEvent extends VolEvent {
  lat: number;
  lng: number;
}

// ═══════════════════════════════════════════════════════════════

export default function EventMap() {
  const navigate = useNavigate();
  const { events, assignments, loading, loadEvents, loadAssignments } = useVolunteerStore();
  const { incidents, activeIncident } = useAuthStore();

  // Filters
  const [incidentFilter, setIncidentFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  // Fly-to target
  const [flyTarget, setFlyTarget] = useState<{ lat: number; lng: number } | null>(null);

  // Detail modal
  const [selectedEvent, setSelectedEvent] = useState<VolEvent | null>(null);

  // Initial load
  const didLoad = useRef(false);
  useEffect(() => {
    if (!didLoad.current) {
      didLoad.current = true;
      loadEvents();
      loadAssignments();
    }
  }, [loadEvents, loadAssignments]);

  useEffect(() => {
    if (activeIncident) setIncidentFilter(String(activeIncident.id));
  }, [activeIncident]);

  // ── Build incident color map ──────────────────────────────

  const incidentColorMap = useMemo(() => {
    const map = new Map<string, string>();
    incidents.forEach((inc, idx) => {
      map.set(String(inc.id), INCIDENT_PALETTE[idx % INCIDENT_PALETTE.length]);
    });
    return map;
  }, [incidents]);

  // ── Parse geo data and filter ─────────────────────────────

  // Parse geo data from notes + attempt geocoding for events without coordinates
  const [geocodedCoords, setGeocodedCoords] = useState<Record<number, { lat: number; lng: number }>>({});
  const geocodedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    // Geocode events that have address but no coordinates in notes
    events.forEach(ev => {
      const extras = parseExtras(ev.notes);
      const lat = Number(extras.lat) || Number(extras.latitude) || 0;
      const lng = Number(extras.lng) || Number(extras.longitude) || 0;
      if (lat && lng) return; // already has coords
      if (geocodedRef.current.has(ev.id)) return; // already attempted
      if (!ev.address) return; // no address to geocode
      geocodedRef.current.add(ev.id);
      fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(ev.address)}&limit=1&countrycodes=us`,
        { headers: { 'User-Agent': 'OpenGov-EMC/1.0' } },
      )
        .then(r => r.json())
        .then(data => {
          if (data.length > 0) {
            setGeocodedCoords(prev => ({
              ...prev,
              [ev.id]: { lat: Number(data[0].lat), lng: Number(data[0].lon) },
            }));
          }
        })
        .catch(() => {});
    });
  }, [events]);

  const allGeoEvents: GeoEvent[] = useMemo(() => {
    return events
      .map(ev => {
        const extras = parseExtras(ev.notes);
        let lat = Number(extras.lat) || Number(extras.latitude) || 0;
        let lng = Number(extras.lng) || Number(extras.longitude) || 0;
        // Fallback to geocoded coordinates
        if ((!lat || !lng) && geocodedCoords[ev.id]) {
          lat = geocodedCoords[ev.id].lat;
          lng = geocodedCoords[ev.id].lng;
        }
        if (!lat || !lng) return null;
        return { ...ev, lat, lng };
      })
      .filter((e): e is GeoEvent => e !== null);
  }, [events, geocodedCoords]);

  // Apply filters to all events (for sidebar list)
  let filteredEvents = events;
  if (incidentFilter !== 'all') filteredEvents = filteredEvents.filter(e => String(e.incidentId) === incidentFilter);
  if (statusFilter !== 'all') filteredEvents = filteredEvents.filter(e => e.status === statusFilter);
  if (search) {
    const q = search.toLowerCase();
    filteredEvents = filteredEvents.filter(e =>
      (e.name || '').toLowerCase().includes(q) ||
      (e.location || '').toLowerCase().includes(q),
    );
  }
  filteredEvents = [...filteredEvents].sort((a, b) => (b.datecreated ?? '').localeCompare(a.datecreated ?? ''));

  // Apply same filters to geo events (for map markers)
  const filteredIds = new Set(filteredEvents.map(e => e.id));
  const filteredGeoEvents = allGeoEvents.filter(e => filteredIds.has(e.id));

  function assignedCount(eventId: number): number {
    return assignments.filter(
      a => String(a.eventId) === String(eventId) && a.status !== 'cancelled' && a.status !== 'declined',
    ).length;
  }

  function hasGeo(ev: VolEvent): boolean {
    return allGeoEvents.some(g => g.id === ev.id);
  }

  function handleListClick(ev: VolEvent) {
    setSelectedEvent(ev);
    const geo = allGeoEvents.find(g => g.id === ev.id);
    if (geo) {
      setFlyTarget({ lat: geo.lat, lng: geo.lng });
    }
  }

  // ── Map center ────────────────────────────────────────────

  const boundsPoints: [number, number][] = filteredGeoEvents.map(e => [e.lat, e.lng]);
  const defaultCenter: [number, number] = boundsPoints.length > 0
    ? boundsPoints[0]
    : [39.8283, -98.5795];

  // ── Select data ───────────────────────────────────────────

  const incidentOptions = [
    { value: 'all', label: 'All Incidents' },
    ...incidents.filter(i => i.id != null).map(i => ({ value: String(i.id), label: i.name })),
  ];

  const statusFilterOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'planned', label: 'Planned' },
    { value: 'active', label: 'Active' },
    { value: 'closed', label: 'Closed' },
  ];

  // ── Render ────────────────────────────────────────────────

  if (loading && events.length === 0) {
    return <Center py="xl"><Loader size="sm" /><Text ml="sm" size="sm" c="dimmed">Loading events...</Text></Center>;
  }

  return (
    <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 180px)', minHeight: 500 }}>
      {/* ── Sidebar ────────────────────────────────────────── */}
      <Paper withBorder radius="sm" style={{ width: 300, minWidth: 260, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '16px 12px 8px' }}>
          <Group justify="space-between" mb={8}>
            <Text fw={600} size="sm">Events ({filteredEvents.length})</Text>
            <ActionIcon variant="subtle" size="xs" onClick={() => { loadEvents(); loadAssignments(); }} title="Refresh">
              <IconRefresh size={14} />
            </ActionIcon>
          </Group>
          <TextInput
            placeholder="Search..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="xs"
            mb={6}
          />
          <div style={{ marginBottom: 4 }}>
            <select
              value={incidentFilter}
              onChange={e => setIncidentFilter(e.target.value)}
              style={{ width: '100%', marginBottom: 4, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-neutral-300)', fontSize: 12, background: 'white' }}
            >
              {incidentOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ width: '100%', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-neutral-300)', fontSize: 12, background: 'white' }}
            >
              {statusFilterOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <ScrollArea style={{ flex: 1 }} px={12} pb={12}>
          <Stack gap={6}>
            {filteredEvents.map(ev => {
              const assigned = assignedCount(ev.id);
              const inc = incidents.find(i => String(i.id) === String(ev.incidentId));
              const color = incidentColorMap.get(String(ev.incidentId)) ?? '#6b7280';
              const geo = hasGeo(ev);
              return (
                <Paper
                  key={ev.id}
                  p="xs"
                  radius="sm"
                  withBorder
                  style={{
                    borderLeft: `3px solid ${color}`,
                    cursor: 'pointer',
                    opacity: geo ? 1 : 0.6,
                  }}
                  onClick={() => handleListClick(ev)}
                >
                  <Text size="xs" fw={600} mb={4} style={{ wordBreak: 'break-word' }}>{ev.name}</Text>
                  <Group gap={4} mb={2}>
                    <Badge size="xs" color={TYPE_COLORS[ev.type] ?? '#939598'} tt="capitalize">{ev.type.replace(/_/g, ' ')}</Badge>
                    <Badge size="xs" color={STATUS_COLORS[ev.status] ?? '#939598'} variant="outline" tt="capitalize">{ev.status}</Badge>
                  </Group>
                  {inc && <Text size="xs" c="dimmed" lineClamp={1}>{inc.name}</Text>}
                  {ev.location && (
                    <Group gap={4}>
                      <IconMapPin size={10} color="#939598" />
                      <Text size="xs" c="dimmed" lineClamp={1}>{ev.location}</Text>
                    </Group>
                  )}
                  {ev.startDate && (
                    <Text size="xs" c="dimmed">{dayjs(ev.startDate).format('MMM D, YYYY')}</Text>
                  )}
                  <Group gap={4}>
                    <IconUsers size={10} color="#939598" />
                    <Text size="xs" c="dimmed">
                      {assigned}{ev.targetVol > 0 ? ` / ${ev.targetVol}` : ''}
                    </Text>
                  </Group>
                  {!geo && <Text size="xs" c="var(--color-error)" mt={2}>No coordinates</Text>}
                </Paper>
              );
            })}
            {filteredEvents.length === 0 && (
              <Text size="xs" c="dimmed" ta="center" py="md">No events match filters.</Text>
            )}
          </Stack>
        </ScrollArea>
      </Paper>

      {/* ── Event Detail Modal ─────────────────────────────── */}
      {selectedEvent && (
        <Modal
          opened={!!selectedEvent}
          onClose={() => setSelectedEvent(null)}
          title={<Title order={4}>{selectedEvent.name}</Title>}
          size="md"
        >
          {(() => {
            const ev = selectedEvent;
            const inc = incidents.find(i => String(i.id) === String(ev.incidentId));
            const assigned = assignedCount(ev.id);
            const geo = allGeoEvents.find(g => g.id === ev.id);
            return (
              <Stack gap="sm">
                <Group gap={6}>
                  <Badge color={TYPE_COLORS[ev.type] ?? '#939598'} tt="capitalize">{ev.type.replace(/_/g, ' ')}</Badge>
                  <Badge color={STATUS_COLORS[ev.status] ?? '#939598'} variant="outline" tt="capitalize">{ev.status}</Badge>
                </Group>
                {inc && (
                  <div>
                    <Text size="xs" c="dimmed" fw={600}>Incident</Text>
                    <Text size="sm">{inc.name}</Text>
                  </div>
                )}
                {ev.location && (
                  <div>
                    <Text size="xs" c="dimmed" fw={600}>Location</Text>
                    <Text size="sm">{ev.location}</Text>
                  </div>
                )}
                {ev.address && (
                  <div>
                    <Text size="xs" c="dimmed" fw={600}>Address</Text>
                    <Text size="sm">{ev.address}</Text>
                  </div>
                )}
                {(ev.startDate || ev.endDate) && (
                  <div>
                    <Text size="xs" c="dimmed" fw={600}>Date</Text>
                    <Text size="sm">
                      {ev.startDate ? dayjs(ev.startDate).format('MMM D, YYYY') : ''}
                      {ev.endDate && ev.endDate !== ev.startDate ? ` — ${dayjs(ev.endDate).format('MMM D, YYYY')}` : ''}
                    </Text>
                  </div>
                )}
                <div>
                  <Text size="xs" c="dimmed" fw={600}>Volunteers</Text>
                  <Text size="sm">{assigned}{ev.targetVol > 0 ? ` / ${ev.targetVol} target` : ' assigned'}</Text>
                </div>
                {ev.reqCerts && (
                  <div>
                    <Text size="xs" c="dimmed" fw={600}>Required Certifications</Text>
                    <Text size="sm">{ev.reqCerts}</Text>
                  </div>
                )}
                {geo && (
                  <div>
                    <Text size="xs" c="dimmed" fw={600}>Coordinates</Text>
                    <Text size="sm">{geo.lat.toFixed(5)}, {geo.lng.toFixed(5)}</Text>
                  </div>
                )}
                <Divider />
                <Button
                  variant="light"
                  fullWidth
                  onClick={() => { setSelectedEvent(null); navigate('/volunteer/events'); }}
                >
                  Go to Events Page
                </Button>
              </Stack>
            );
          })()}
        </Modal>
      )}

      {/* ── Map ────────────────────────────────────────────── */}
      <div style={{ flex: 1, borderRadius: 'var(--radius, 10px)', overflow: 'hidden' }}>
        <MapContainer
          center={defaultCenter}
          zoom={boundsPoints.length > 0 ? 10 : 4}
          style={{ height: '100%', width: '100%' }}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Street">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Satellite">
              <TileLayer
                attribution='&copy; Esri'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Topo">
              <TileLayer
                attribution='&copy; OpenTopoMap'
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Dark">
              <TileLayer
                attribution='&copy; CartoDB'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
            </LayersControl.BaseLayer>
          </LayersControl>
          {boundsPoints.length > 0 && !flyTarget && <FitBounds points={boundsPoints} />}
          {flyTarget && <FlyTo lat={flyTarget.lat} lng={flyTarget.lng} />}

          {filteredGeoEvents.map(ev => {
            const color = incidentColorMap.get(String(ev.incidentId)) ?? '#6b7280';
            const assigned = assignedCount(ev.id);
            const inc = incidents.find(i => String(i.id) === String(ev.incidentId));
            return (
              <Marker
                key={ev.id}
                position={[ev.lat, ev.lng]}
                icon={createColoredIcon(color)}
              >
                <Popup>
                  <Stack gap={4} style={{ minWidth: 240 }}>
                    <Text fw={600} size="sm">{ev.name}</Text>
                    <Group gap={6}>
                      <Badge size="xs" color={TYPE_COLORS[ev.type] ?? '#939598'} tt="capitalize">{ev.type.replace(/_/g, ' ')}</Badge>
                      <Badge size="xs" color={STATUS_COLORS[ev.status] ?? '#939598'} variant="outline" tt="capitalize">{ev.status}</Badge>
                    </Group>
                    {inc && <Text size="xs" c="dimmed">{inc.name}</Text>}
                    {ev.location && (
                      <Group gap={4}>
                        <IconMapPin size={10} color="#888" />
                        <Text size="xs" c="dimmed">{ev.location}</Text>
                      </Group>
                    )}
                    {ev.address && ev.address !== ev.location && (
                      <Text size="xs" c="dimmed">{ev.address}</Text>
                    )}
                    {ev.startDate && (
                      <Group gap={4}>
                        <IconCalendarEvent size={10} color="#888" />
                        <Text size="xs" c="dimmed">
                          {dayjs(ev.startDate).format('MMM D, YYYY')}
                          {ev.endDate && ev.endDate !== ev.startDate ? ` — ${dayjs(ev.endDate).format('MMM D, YYYY')}` : ''}
                        </Text>
                      </Group>
                    )}
                    <Group gap={4}>
                      <IconUsers size={12} />
                      <Text size="xs">
                        {assigned}{ev.targetVol > 0 ? ` / ${ev.targetVol}` : ''} volunteers
                        {ev.targetVol > 0 && assigned >= ev.targetVol && (
                          <Badge size="xs" color="var(--color-error)" ml={4}>Full</Badge>
                        )}
                      </Text>
                    </Group>
                    {ev.reqCerts && (
                      <Group gap={4}>
                        <IconShieldCheck size={10} color="#888" />
                        <Text size="xs" c="dimmed">Certs: {ev.reqCerts}</Text>
                      </Group>
                    )}
                    <Divider my={2} />
                    <Button
                      size="compact-xs"
                      variant="light"
                      fullWidth
                      onClick={() => navigate('/volunteer/events')}
                    >
                      View Event Details
                    </Button>
                  </Stack>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}
